import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaTrash, FaEdit, FaPlus, FaSave, FaTimes, FaStar, FaImage } from 'react-icons/fa';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { galleryImages as initialImages } from '../../data/galleryImages';

const ImageUpload = () => {
  const [images, setImages] = useLocalStorage('tinytimes-gallery', initialImages);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newImage, setNewImage] = useState({
    title: '',
    caption: '',
    category: 'readers',
    featured: false,
    src: null,
  });

  const categories = [
    { id: 'readers', label: '👧 Readers', color: 'bg-blue-500' },
    { id: 'events', label: '🎉 Events', color: 'bg-pink-500' },
    { id: 'artwork', label: '🎨 Artwork', color: 'bg-purple-500' },
    { id: 'activities', label: '✂️ Activities', color: 'bg-orange-500' },
    { id: 'team', label: '👥 Team', color: 'bg-green-500' },
  ];

  // Handle image upload
  const handleImageUpload = (id, file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        
        if (id === 'new') {
          setNewImage(prev => ({ ...prev, src: imageUrl }));
        } else {
          setImages(prev => prev.map(img => 
            img.id === id ? { ...img, src: imageUrl } : img
          ));
        }
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload an image file');
    }
  };

  // Add new image
  const handleAddImage = () => {
    if (!newImage.title.trim() || !newImage.src) {
      alert('Please enter a title and upload an image');
      return;
    }

    const image = {
      id: Date.now(),
      ...newImage,
      alt: newImage.title,
    };

    setImages(prev => [...prev, image]);
    setNewImage({
      title: '',
      caption: '',
      category: 'readers',
      featured: false,
      src: null,
    });
    setIsAddingNew(false);
  };

  // Delete image
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      setImages(prev => prev.filter(img => img.id !== id));
    }
  };

  // Toggle featured
  const toggleFeatured = (id) => {
    setImages(prev => prev.map(img => 
      img.id === id ? { ...img, featured: !img.featured } : img
    ));
  };

  // Update image
  const handleUpdate = (id, updates) => {
    setImages(prev => prev.map(img => 
      img.id === id ? { ...img, ...updates } : img
    ));
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-display font-bold text-primary-green-dark">
          Gallery Management
        </h2>
        <motion.button
          onClick={() => setIsAddingNew(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary flex items-center gap-2"
        >
          <FaPlus />
          <span>Add Image</span>
        </motion.button>
      </div>

      {/* Add New Image Form */}
      <AnimatePresence>
        {isAddingNew && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card bg-accent-blue/10 border-2 border-primary-green/30"
          >
            <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
              Add New Image
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image Upload */}
              <div>
                <label className="block font-semibold text-primary-green-dark mb-2">
                  Image *
                </label>
                <div className="border-2 border-dashed border-primary-green/30 rounded-lg p-4 text-center hover:border-primary-green transition-colors aspect-video flex items-center justify-center overflow-hidden">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('new', e.target.files[0])}
                    className="hidden"
                    id="new-image-upload"
                  />
                  <label htmlFor="new-image-upload" className="cursor-pointer w-full h-full flex items-center justify-center">
                    {newImage.src ? (
                      <img
                        src={newImage.src}
                        alt="Preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <div>
                        <FaImage className="text-4xl text-primary-green mx-auto mb-2" />
                        <div className="font-semibold text-primary-green-dark">
                          Click to upload
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block font-semibold text-primary-green-dark mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newImage.title}
                    onChange={(e) => setNewImage(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Image title"
                    className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-primary-green-dark mb-2">
                    Caption
                  </label>
                  <input
                    type="text"
                    value={newImage.caption}
                    onChange={(e) => setNewImage(prev => ({ ...prev, caption: e.target.value }))}
                    placeholder="Brief caption"
                    className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-primary-green-dark mb-2">
                    Category
                  </label>
                  <select
                    value={newImage.category}
                    onChange={(e) => setNewImage(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none bg-white"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newImage.featured}
                    onChange={(e) => setNewImage(prev => ({ ...prev, featured: e.target.checked }))}
                    className="w-5 h-5 accent-primary-green"
                  />
                  <span className="font-semibold text-primary-green-dark">
                    ⭐ Featured Image
                  </span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <motion.button
                onClick={handleAddImage}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary flex items-center gap-2"
              >
                <FaSave />
                <span>Save Image</span>
              </motion.button>
              <motion.button
                onClick={() => {
                  setIsAddingNew(false);
                  setNewImage({ title: '', caption: '', category: 'readers', featured: false, src: null });
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-button font-semibold flex items-center gap-2"
              >
                <FaTimes />
                <span>Cancel</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Images Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {images.map((image) => (
          <motion.div
            key={image.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-0 overflow-hidden group"
          >
            {/* Image Preview */}
            <div className="relative aspect-square">
              <img
                src={image.src}
                alt={image.alt || image.title}
                className="w-full h-full object-cover"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <motion.button
                  onClick={() => setEditingId(image.id)}
                  whileHover={{ scale: 1.1 }}
                  className="p-3 bg-white rounded-full text-primary-green"
                >
                  <FaEdit />
                </motion.button>
                <motion.button
                  onClick={() => handleDelete(image.id)}
                  whileHover={{ scale: 1.1 }}
                  className="p-3 bg-white rounded-full text-red-500"
                >
                  <FaTrash />
                </motion.button>
              </div>

              {/* Featured Badge */}
              {image.featured && (
                <div className="absolute top-2 right-2 bg-accent-yellow text-primary-green-dark text-xs font-bold px-2 py-1 rounded-full">
                  ⭐ Featured
                </div>
              )}

              {/* Category Badge */}
              <div className={`absolute top-2 left-2 ${categories.find(c => c.id === image.category)?.color || 'bg-gray-500'} text-white text-xs font-bold px-2 py-1 rounded-full capitalize`}>
                {image.category}
              </div>
            </div>

            {/* Info */}
            <div className="p-3">
              <h3 className="font-display font-bold text-primary-green-dark truncate">
                {image.title}
              </h3>
              <p className="text-sm text-neutral-gray-dark truncate">
                {image.caption || 'No caption'}
              </p>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => toggleFeatured(image.id)}
                  className={`flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs font-semibold transition-colors ${
                    image.featured 
                      ? 'bg-accent-yellow text-primary-green-dark' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaStar />
                  <span>{image.featured ? 'Featured' : 'Feature'}</span>
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-20">
          <div className="text-8xl mb-4">🖼️</div>
          <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-2">
            No images yet
          </h3>
          <p className="text-neutral-gray-dark">
            Upload your first image to get started!
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;